import { type ApiError } from "api/error/ApiError";
import axiosInstance, { type AxiosResponse, type AxiosRequestConfig } from "components/Auth/axiosConfig";

export class BaseController {
    public readonly apiHost: string;
    public readonly version: string;
    private headers: Record<string, string>;
    private isFeatureEnabled: boolean;

    constructor(apiHost: string, version = 'v1', isFeatureEnabled = true) {
        this.apiHost = apiHost;
        this.version = version;
        this.headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };
        this.isFeatureEnabled = isFeatureEnabled;
    }

    public setHeaders(headers: Record<string, string>) {
        this.headers = { ...this.headers, ...headers };
    }

    public getEndpoint(path: string, version = this.version): string {
        return `${this.apiHost}/${version}/${path}`;
    }

    protected async resolveApiHost(_version = this.version): Promise<string> {
        return this.apiHost;
    }

    async getEndpointAsync(path: string, version = this.version): Promise<string> {
        return `${await this.resolveApiHost(version)}/${version}/${path}`;
    }

    async sendRequest<T = any, D = any>(method: 'get' | 'post' | 'put' | 'patch' | 'delete', url: string, data?: D, config?: Partial<AxiosRequestConfig>): Promise<T> {
        const contentType = config?.headers?.['Content-Type'] || this.headers['Content-Type'];
        let processedData = data;
        if (contentType === 'multipart/form-data' && data) {
            const formData = new FormData();
            Object.entries(data).forEach(([key, val]) => {
                const value = !['metadata', 'licenses'].includes(key) ? val : JSON.stringify(val) as any;
                if (!!value) formData.append(key, value);
            });
            processedData = formData as D;
        }
        if (!this.isFeatureEnabled) {
            console.debug(`Feature is disabled for ${method.toUpperCase()} ${url}`);
            return Promise.resolve(null as T);
        }
        const result = await axiosInstance.request<T, AxiosResponse<T, ApiError>, D>({
            method,
            url,
            data: processedData,
            headers: this.headers,
            ...config
        });
        return result.data;
    }

    async get<T = any, D = any>(url: string, params?: D, config?: Partial<AxiosRequestConfig>): Promise<T> {
        return this.sendRequest<T, D>('get', url, undefined, { ...config, params });
    }

    async post<T = any, D = any>(url: string, data?: D, config?: Partial<AxiosRequestConfig>): Promise<T> {
        return this.sendRequest<T, D>('post', url, data, config);
    }

    async put<T = any, D = any>(url: string, data?: D, config?: Partial<AxiosRequestConfig>): Promise<T> {
        return this.sendRequest<T, D>('put', url, data, config);
    }

    async delete<T = any>(url: string, config?: Partial<AxiosRequestConfig>): Promise<T> {
        return this.sendRequest<T, void>('delete', url, undefined, config);
    }
    
    async patch<T = any, D = any>(url: string, data?: D, config?: Partial<AxiosRequestConfig>): Promise<T> {
        return this.sendRequest<T, D>('patch', url, data, config);
    }
}
